import { Injectable, OnDestroy } from '@angular/core';
import {
  CliffhangerAnalyticsService,
  ContactInsightService,
  GiaAnalyticsService,
} from '@g99-product-web-ui/shared-service';
import {
  formatDateToSqlTimestamp,
  getDateDiffUnit,
  setEndOfDayTime,
} from '@g99-product-web-ui/shared-util-lib';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class WebMcpToolsService implements OnDestroy {
  private _abortController = new AbortController();
  private _registered = false;

  constructor(
    private readonly _contactInsightService: ContactInsightService,
    private readonly _cliffhangerService: CliffhangerAnalyticsService,
    private readonly _giaAnalyticsService: GiaAnalyticsService
  ) {}

  public registerTools(): void {
    if (this._registered) {
      return;
    }

    const modelContext = document.modelContext ?? navigator.modelContext;
    if (!modelContext) {
      return;
    }

    this._registerLeadInsightsTools(modelContext);
    this._registerCliffhangerTools(modelContext);
    this._registerGiaAnalyticsTools(modelContext);
    this._registered = true;
  }

  public deregisterTools(): void {
    if (!this._registered) {
      return;
    }
    this._abortController.abort();
    this._abortController = new AbortController(); // reset so next login can re-register
    this._registered = false;
  }

  ngOnDestroy(): void {
    this.deregisterTools();
  }

  /** Registers a single tool with signal support, falls back without signal on error. */
  private _register(
    ctx: NonNullable<Document['modelContext']>,
    definition: object
  ): void {
    try {
      ctx.registerTool(definition, { signal: this._abortController.signal });
    } catch {
      try {
        ctx.registerTool(definition);
      } catch {
        // registration failed silently
      }
    }
  }

  private _registerLeadInsightsTools(
    ctx: NonNullable<Document['modelContext']>
  ): void {
    this._register(ctx, {
      name: 'get_lead_insights',
      description:
        'Fetch contact/lead insights report for a given date range. Returns total contacts, trend, top sources, UTM summary, metrics (bookingRate, conversionRate, averageDaysToBook), and time series data.',
      inputSchema: {
        type: 'object',
        properties: {
          startDate: {
            type: 'string',
            description: 'Start date in YYYY-MM-DD format, minimum 2021-01-01',
          },
          endDate: {
            type: 'string',
            description: 'End date in YYYY-MM-DD format',
          },
          reportType: {
            type: 'string',
            enum: ['LEAD', 'CUSTOMER'],
            description: 'Type of contacts to report on. Defaults to LEAD.',
          },
        },
        required: ['startDate', 'endDate'],
      },
      execute: async (args: {
        startDate: string;
        endDate: string;
        reportType?: 'LEAD' | 'CUSTOMER';
      }) => {
        try {
          const start = new Date(args.startDate);
          const end = new Date(args.endDate);
          return await firstValueFrom(
            this._contactInsightService.getContactInsights({
              reportType: args.reportType ?? 'LEAD',
              startDate: formatDateToSqlTimestamp(start),
              endDate: formatDateToSqlTimestamp(setEndOfDayTime(end)),
              unit: getDateDiffUnit(start, end),
            })
          );
        } catch (err) {
          return {
            error: 'Failed to fetch lead insights',
            details: String(err),
          };
        }
      },
    });

    this._register(ctx, {
      name: 'get_lead_insight_detail_by_source',
      description:
        'Fetch detailed lead/contact breakdown for a specific traffic source within a date range. Returns sub-source breakdown and per-page lead counts.',
      inputSchema: {
        type: 'object',
        properties: {
          source: {
            type: 'string',
            description:
              'Traffic source name (e.g. "Google", "Facebook", "Direct")',
          },
          startDate: {
            type: 'string',
            description: 'Start date in YYYY-MM-DD format',
          },
          endDate: {
            type: 'string',
            description: 'End date in YYYY-MM-DD format',
          },
          reportType: {
            type: 'string',
            enum: ['LEAD', 'CUSTOMER'],
            description: 'Type of contacts. Defaults to LEAD.',
          },
        },
        required: ['source', 'startDate', 'endDate'],
      },
      execute: async (args: {
        source: string;
        startDate: string;
        endDate: string;
        reportType?: 'LEAD' | 'CUSTOMER';
      }) => {
        try {
          const start = new Date(args.startDate);
          const end = new Date(args.endDate);
          return await firstValueFrom(
            this._contactInsightService.getContactDetailedInsightBySource(
              args.source,
              {
                reportType: args.reportType ?? 'LEAD',
                startDate: formatDateToSqlTimestamp(start),
                endDate: formatDateToSqlTimestamp(setEndOfDayTime(end)),
                unit: getDateDiffUnit(start, end),
              }
            )
          );
        } catch (err) {
          return {
            error: 'Failed to fetch lead detail by source',
            details: String(err),
          };
        }
      },
    });
  }

  private _registerCliffhangerTools(
    ctx: NonNullable<Document['modelContext']>
  ): void {
    this._register(ctx, {
      name: 'get_cliffhanger_analytics',
      description:
        'Fetch Cliffhanger analytics summary for a date range. Returns engagement metrics and summary data.',
      inputSchema: {
        type: 'object',
        properties: {
          startDate: {
            type: 'string',
            description: 'Start date in YYYY-MM-DD format',
          },
          endDate: {
            type: 'string',
            description: 'End date in YYYY-MM-DD format',
          },
        },
        required: ['startDate', 'endDate'],
      },
      execute: async (args: { startDate: string; endDate: string }) => {
        try {
          const start = new Date(args.startDate);
          const end = new Date(args.endDate);
          return await firstValueFrom(
            this._cliffhangerService.getCliffhangerAnalytics(
              formatDateToSqlTimestamp(start),
              formatDateToSqlTimestamp(setEndOfDayTime(end))
            )
          );
        } catch (err) {
          return {
            error: 'Failed to fetch Cliffhanger analytics',
            details: String(err),
          };
        }
      },
    });

    this._register(ctx, {
      name: 'get_cliffhanger_chart_data',
      description:
        'Fetch Cliffhanger chart data for a specific icon/metric and date range. Use after get_cliffhanger_analytics to drill into a specific metric.',
      inputSchema: {
        type: 'object',
        properties: {
          startDate: {
            type: 'string',
            description: 'Start date in YYYY-MM-DD format',
          },
          endDate: {
            type: 'string',
            description: 'End date in YYYY-MM-DD format',
          },
          selectedIcon: {
            type: 'string',
            description:
              'The icon/metric identifier to retrieve chart data for',
          },
        },
        required: ['startDate', 'endDate', 'selectedIcon'],
      },
      execute: async (args: {
        startDate: string;
        endDate: string;
        selectedIcon: string;
      }) => {
        try {
          const start = new Date(args.startDate);
          const end = new Date(args.endDate);
          return await firstValueFrom(
            this._cliffhangerService.getCliffhangerChartData(
              formatDateToSqlTimestamp(start),
              formatDateToSqlTimestamp(setEndOfDayTime(end)),
              args.selectedIcon
            )
          );
        } catch (err) {
          return {
            error: 'Failed to fetch Cliffhanger chart data',
            details: String(err),
          };
        }
      },
    });
  }

  private _registerGiaAnalyticsTools(
    ctx: NonNullable<Document['modelContext']>
  ): void {
    this._register(ctx, {
      name: 'get_gia_analytics_usage',
      description:
        'Fetch GIA (AI assistant) usage analytics. Returns conversation counts, engagement metrics, and usage patterns for the specified time period.',
      inputSchema: {
        type: 'object',
        properties: {
          filter: {
            type: 'string',
            enum: ['day', 'week', 'month', 'year'],
            description:
              'Time period filter for the usage report. Defaults to "week".',
          },
        },
      },
      execute: async (args: { filter?: string }) => {
        try {
          return await firstValueFrom(
            this._giaAnalyticsService.getGiaAnalyticsUsage(
              args.filter ?? 'week'
            )
          );
        } catch (err) {
          return {
            error: 'Failed to fetch GIA analytics usage',
            details: String(err),
          };
        }
      },
    });

    this._register(ctx, {
      name: 'get_gia_analytics_daily',
      description:
        'Fetch GIA day-by-day activity analytics for the past N days. Useful for trend analysis and identifying usage patterns over time.',
      inputSchema: {
        type: 'object',
        properties: {
          days: {
            type: 'number',
            description:
              'Number of past days to retrieve data for. Defaults to 7.',
          },
        },
      },
      execute: async (args: { days?: number }) => {
        try {
          return await firstValueFrom(
            this._giaAnalyticsService.getGiaAnalyticsDaily(args.days ?? 7)
          );
        } catch (err) {
          return {
            error: 'Failed to fetch GIA daily analytics',
            details: String(err),
          };
        }
      },
    });
  }
}
